import React from "react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Eye, Check, X, Pencil, Filter } from "lucide-react";
import { InfluencerSubmission, SubmissionStatus, SubmissionMediaItem } from "@/lib/interfaces";
import { axiosPrivate } from "@/api/axios";
import { toast } from "sonner";
import { Row } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { influencerSubmissionColumns } from "@/lib/columns";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import SubmissionDetailDialog from "./SubmissionDetailDialog";
import SubmissionEditDialog from "./SubmissionEditDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoreHorizontal } from "lucide-react";

type SubmissionWithAlternateIds = InfluencerSubmission & {
  influencerId?: number | string | null;
  influencer_name?: string | null;
  influencer_username?: string | null;
  user_id?: number | string | null;
  created_by?: number | string | null;
  created_by_id?: number | string | null;
  author_id?: number | string | null;
  member_name?: string | null;
  username?: string | null;
  user?: { id?: number | string | null };
};

const toValidId = (value: unknown): number | null => {
  const normalized = Number(value);
  if (Number.isNaN(normalized) || normalized <= 0) return null;
  return normalized;
};

const resolveSubmissionUserId = (submission: SubmissionWithAlternateIds): number | null => {
  const candidates: unknown[] = [
    submission.influencer_id,
    submission.influencerId,
    submission.influencer?.id,
    submission.user_id,
    submission.created_by,
    submission.created_by_id,
    submission.author_id,
    submission.user?.id,
  ];

  for (const candidate of candidates) {
    const id = toValidId(candidate);
    if (id) return id;
  }
  return null;
};

const resolveSubmissionDirectName = (submission: SubmissionWithAlternateIds): string | null => {
  return (
    submission.influencer_name ||
    submission.influencer_username ||
    submission.member_name ||
    submission.username ||
    null
  );
};

const toUsableDisplayName = (...values: Array<string | null | undefined>): string | null => {
  for (const raw of values) {
    const value = raw?.trim();
    if (!value) continue;
    // API sometimes returns placeholder values (e.g. "-") for missing profile fields.
    if (["-", "n/a", "na", "null", "undefined"].includes(value.toLowerCase())) continue;
    return value;
  }
  return null;
};

function InfluencerSubmissionsPage() {
  const queryClient = useQueryClient();
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [selectedSubmission, setSelectedSubmission] = React.useState<InfluencerSubmission | null>(null);
  const [confirmAction, setConfirmAction] = React.useState<"approve" | "reject" | null>(null);
  const confirmActionButtonRef = React.useRef<() => void>(() => {});

  // Server-side filters
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [fromDate, setFromDate] = React.useState<string>("");
  const [influencerIdFilter, setInfluencerIdFilter] = React.useState<string>("");

  const { data: submissions = [], isLoading: isSubmissionsLoading } = useQuery<InfluencerSubmission[]>({
    queryKey: ["influencer-submissions", statusFilter, fromDate, influencerIdFilter],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (fromDate) params.from_date = new Date(fromDate).toISOString();
      if (influencerIdFilter) params.influencer_id = influencerIdFilter;
      const res = await axiosPrivate.get("/admin/influencer/submissions", { params });
      if (!res.data?.success) {
        toast.error("Failed to fetch submissions. Please try again later.");
        throw new Error("Failed to fetch submissions.");
      }
      return res.data.data || [];
    },
    placeholderData: [],
  });

  const { data: users = [] } = useQuery<
    Array<{ id: number; username?: string | null; member_name?: string | null; email?: string | null }>
  >({
    queryKey: ["users", "name-map"],
    queryFn: async () => {
      const res = await axiosPrivate.get("/users/");
      if (!res?.data?.success) return [];
      const allUsers = res.data.data as Array<{
        id: number;
        username?: string | null;
        member_name?: string | null;
        email?: string | null;
      }>;
      return allUsers;
    },
    placeholderData: [],
  });

  const influencerNameById = React.useMemo(() => {
    const map = new Map<number, string>();
    for (const user of users) {
      const normalizedId = Number(user.id);
      if (Number.isNaN(normalizedId)) continue;
      const displayName = toUsableDisplayName(user.member_name, user.username, user.email);
      if (displayName) {
        map.set(normalizedId, displayName);
      }
    }
    return map;
  }, [users]);

  const data = React.useMemo(() => {
    return submissions.map((rawSubmission) => {
      const submission = rawSubmission as SubmissionWithAlternateIds;
      const resolvedUserId = resolveSubmissionUserId(submission);
      const mappedName = resolvedUserId ? influencerNameById.get(resolvedUserId) : undefined;
      const directName = toUsableDisplayName(resolveSubmissionDirectName(submission));
      const displayName = toUsableDisplayName(mappedName, directName);

      if (!displayName) {
        return rawSubmission;
      }

      return {
        ...rawSubmission,
        influencer: {
          id: resolvedUserId ?? rawSubmission.influencer_id,
          username: displayName,
          email: rawSubmission.influencer?.email,
          phone_number: rawSubmission.influencer?.phone_number,
        },
      };
    });
  }, [submissions, influencerNameById]);

  const isLoading = isSubmissionsLoading;

  const approveSubmissionMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await axiosPrivate.put(`/admin/influencer/submissions/${id}/approve`);
      if (!res.data?.success) throw new Error("Failed to approve submission.");
      return res.data;
    },
    onMutate: async () => {
      toast.loading("Approving submission...");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["influencer-submissions"] });
      toast.dismiss();
      toast.success("Submission approved.");
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to approve submission. Please try again later.");
    },
  });

  const rejectSubmissionMutation = useMutation({
    mutationFn: async ({ id, feedback }: { id: number; feedback: string }) => {
      const res = await axiosPrivate.put(`/admin/influencer/submissions/${id}/reject`, {
        feedback,
      });
      if (!res.data?.success) throw new Error("Failed to reject submission.");
      return res.data;
    },
    onMutate: async () => {
      toast.loading("Rejecting submission...");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["influencer-submissions"] });
      toast.dismiss();
      toast.info("Submission rejected.");
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to reject submission. Please try again later.");
    },
  });

  const editSubmissionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: {
      caption?: string;
      tags?: string[];
      media?: SubmissionMediaItem[];
      platform?: string;
      expected_publication_date?: string | null;
      objective?: string | null;
    } }) => {
      const res = await axiosPrivate.put(`/admin/influencer/submissions/${id}/edit`, data);
      if (!res.data?.success) throw new Error("Failed to edit submission.");
      return res.data;
    },
    onMutate: async () => {
      toast.loading("Saving changes...");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["influencer-submissions"] });
      toast.dismiss();
      toast.success("Submission updated.");
      setEditDialogOpen(false);
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to save changes. Please try again later.");
    },
  });

  function handleApprove(id: number) {
    approveSubmissionMutation.mutate(id);
  }

  function handleReject(id: number, feedback: string) {
    rejectSubmissionMutation.mutate({ id, feedback });
  }

  function handleEdit(id: number, data: {
    caption?: string;
    tags?: string[];
    media?: SubmissionMediaItem[];
    platform?: string;
    expected_publication_date?: string | null;
    objective?: string | null;
  }) {
    editSubmissionMutation.mutate({ id, data });
  }

  const actions = {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }: { row: Row<InfluencerSubmission> }) => {
      const submission = row.original;
      const isPending = submission.status === "pending";

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                setSelectedSubmission(submission);
                setDetailDialogOpen(true);
              }}
            >
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setSelectedSubmission(submission);
                setEditDialogOpen(true);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            {isPending && (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedSubmission(submission);
                    setConfirmAction("approve");
                    confirmActionButtonRef.current = () => handleApprove(submission.id);
                    setOpenConfirmDialog(true);
                  }}
                  className="text-green-600"
                >
                  <Check className="mr-2 h-4 w-4" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedSubmission(submission);
                    setConfirmAction("reject");
                    // Rejection requires feedback via dialog, handled in SubmissionDetailDialog
                    setDetailDialogOpen(true);
                  }}
                  className="text-red-600"
                >
                  <X className="mr-2 h-4 w-4" />
                  Reject
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  };

  const columns = [...influencerSubmissionColumns, actions];

  const hasActiveFilters = statusFilter !== "all" || !!fromDate || !!influencerIdFilter;

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Influencer Submissions</h1>
      <div className="flex items-center justify-between">
        <p className="text-lg">Review and manage influencer content submissions</p>
      </div>

      {/* Server-side filter bar */}
      <div className="flex flex-wrap gap-3 items-center p-3 bg-muted/40 rounded-lg border">
        <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="w-44"
          title="From date"
        />
        <Input
          type="text"
          value={influencerIdFilter}
          onChange={(e) => setInfluencerIdFilter(e.target.value)}
          className="w-44"
          placeholder="Influencer ID"
        />
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStatusFilter("all");
              setFromDate("");
              setInfluencerIdFilter("");
            }}
          >
            Reset filters
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data}
          placeholder="Filter submissions"
          filterColumns={["caption", "status", "platform"]}
        />
      )}
      <SubmissionDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        submission={selectedSubmission}
        onApprove={handleApprove}
        onReject={handleReject}
      />
      <SubmissionEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        submission={selectedSubmission}
        onSave={handleEdit}
        isSaving={editSubmissionMutation.isPending}
      />
      <ConfirmDialog
        actionButtonRef={confirmActionButtonRef}
        open={openConfirmDialog}
        onOpenChange={setOpenConfirmDialog}
        isLoading={approveSubmissionMutation.isPending || rejectSubmissionMutation.isPending}
        AlertdialogDescription={
          confirmAction === "approve"
            ? "Are you sure you want to approve this submission? It will be visible to all users."
            : "Are you sure you want to reject this submission? The user will be notified."
        }
      />
    </div>
  );
}

export default InfluencerSubmissionsPage;
